import React, { useState } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { submitContactForm } from '../data/contact';

const ContactForm = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const shouldReduceMotion = useReducedMotion();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  React.useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const result = await submitContactForm(formData);
      
      if (result.success) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', subject: '', message: '' });
        
        // Close form after successful submission
        setTimeout(() => {
          onClose();
          setSubmitStatus(null);
        }, 2000);
      } else {
        setSubmitStatus('error');
      }
      
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Mobile-optimized animation helper
  const mobileOptimizedMotion = (desktopProps, mobileProps = {}) => {
    if (shouldReduceMotion || isMobile) {
      return {
        initial: { opacity: 0 },
        animate: { opacity: 1 },
        transition: { duration: 0.2 },
        ...mobileProps
      };
    }
    return desktopProps;
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: shouldReduceMotion || isMobile ? 0.2 : 0.3 }}
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={(e) => e.target === e.currentTarget && onClose()}
        >
          <motion.div
            {...mobileOptimizedMotion(
              {
                initial: { opacity: 0, scale: 0.9, y: 20 },
                animate: { opacity: 1, scale: 1, y: 0 },
                exit: { opacity: 0, scale: 0.9, y: 20 },
                transition: { duration: 0.3, ease: "easeOut" }
              },
              {
                initial: { opacity: 0 },
                animate: { opacity: 1 },
                exit: { opacity: 0 },
                transition: { duration: 0.2 }
              }
            )}
          >
            <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto glass-modal">
              <CardHeader className="flex flex-row items-center justify-between">
                <motion.div
                  {...mobileOptimizedMotion(
                    {
                      initial: { opacity: 0, x: -20 },
                      animate: { opacity: 1, x: 0 },
                      transition: { duration: 0.4, delay: 0.1 }
                    }
                  )}
                >
                  <CardTitle className="text-2xl text-white">Get In Touch</CardTitle>
                </motion.div>
                <motion.div
                  {...mobileOptimizedMotion(
                    {
                      initial: { opacity: 0, scale: 0 },
                      animate: { opacity: 1, scale: 1 },
                      transition: { duration: 0.3, delay: 0.2 },
                      whileHover: { scale: 1.1 },
                      whileTap: { scale: 0.95 }
                    },
                    {
                      whileTap: { scale: 0.95 }
                    }
                  )}
                >
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={onClose}
                    className="h-8 w-8 text-white/80 hover:text-white hover:bg-white/10"
                  >
                    <span className="material-icons-outlined">close</span>
                  </Button>
                </motion.div>
              </CardHeader>
              <CardContent>
                <motion.form 
                  onSubmit={handleSubmit} 
                  className="space-y-6"
                  {...mobileOptimizedMotion(
                    {
                      initial: { opacity: 0, y: 20 },
                      animate: { opacity: 1, y: 0 },
                      transition: { duration: 0.4, delay: 0.2 }
                    }
                  )}
                >
                  <motion.div 
                    className="grid grid-cols-1 md:grid-cols-2 gap-4"
                    {...mobileOptimizedMotion(
                      {
                        initial: { opacity: 0, y: 20 },
                        animate: { opacity: 1, y: 0 },
                        transition: { duration: 0.4, delay: 0.3 }
                      }
                    )}
                  >
                    <div className="space-y-2">
                      <label htmlFor="name" className="text-sm font-medium text-white/90">
                        Name *
                      </label>
                      <Input
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        placeholder="Your full name"
                      />
                    </div>
                    <div className="space-y-2">
                      <label htmlFor="email" className="text-sm font-medium text-white/90">
                        Email *
                      </label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        placeholder="your.email@example.com"
                      />
                    </div>
                  </motion.div>
                  
                  <motion.div 
                    className="space-y-2"
                    {...mobileOptimizedMotion(
                      {
                        initial: { opacity: 0, y: 20 },
                        animate: { opacity: 1, y: 0 },
                        transition: { duration: 0.4, delay: 0.4 }
                      }
                    )}
                  >
                    <label htmlFor="subject" className="text-sm font-medium text-white/90">
                      Subject *
                    </label>
                    <Input
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      placeholder="What's this about?"
                    />
                  </motion.div>
                  
                  <motion.div 
                    className="space-y-2"
                    {...mobileOptimizedMotion(
                      {
                        initial: { opacity: 0, y: 20 },
                        animate: { opacity: 1, y: 0 },
                        transition: { duration: 0.4, delay: 0.5 }
                      }
                    )}
                  >
                    <label htmlFor="message" className="text-sm font-medium text-white/90">
                      Message *
                    </label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      placeholder="Tell me about your project or just say hello..."
                      rows={6}
                    />
                  </motion.div>

                  <AnimatePresence>
                    {submitStatus === 'success' && (
                      <motion.div 
                        initial={{ opacity: 0, y: -10, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -10, scale: 0.95 }}
                        transition={{ duration: 0.3 }}
                        className="flex items-center gap-2 text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20 p-3 rounded-md"
                      >
                        <span className="material-icons-outlined text-sm">check_circle</span>
                        <span className="text-sm">Message sent successfully! I'll get back to you soon.</span>
                      </motion.div>
                    )}

                    {submitStatus === 'error' && (
                      <motion.div 
                        initial={{ opacity: 0, y: -10, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -10, scale: 0.95 }}
                        transition={{ duration: 0.3 }}
                        className="flex items-center gap-2 text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 p-3 rounded-md"
                      >
                        <span className="material-icons-outlined text-sm">error</span>
                        <span className="text-sm">Failed to send message. Please try again or contact me directly.</span>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  <motion.div 
                    className="flex gap-3 pt-4"
                    {...mobileOptimizedMotion(
                      {
                        initial: { opacity: 0, y: 20 },
                        animate: { opacity: 1, y: 0 },
                        transition: { duration: 0.4, delay: 0.6 }
                      }
                    )}
                  >
                    <motion.div
                      whileHover={!isMobile ? { scale: 1.02 } : {}}
                      whileTap={{ scale: 0.98 }}
                      className="flex-1"
                    >
                      <Button
                        type="submit"
                        disabled={isSubmitting}
                        className="w-full bg-primary hover:bg-primary/90"
                      >
                        {isSubmitting ? (
                          <>
                            <motion.div 
                              animate={{ rotate: 360 }}
                              transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                              className="rounded-full h-4 w-4 border-b-2 border-white mr-2"
                            ></motion.div>
                            Sending...
                          </>
                        ) : (
                          <>
                            <span className="material-icons-outlined text-sm mr-2">send</span>
                            Send Message
                          </>
                        )}
                      </Button>
                    </motion.div>
                    <motion.div
                      whileHover={!isMobile ? { scale: 1.02 } : {}}
                      whileTap={{ scale: 0.98 }}
                    >
                      <Button
                        type="button"
                        variant="outline"
                        onClick={onClose}
                        disabled={isSubmitting}
                      >
                        Cancel
                      </Button>
                    </motion.div>
                  </motion.div>
                </motion.form>
              </CardContent>
            </Card>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ContactForm;